import React from 'react';
import { Box, Tooltip, Typography, Zoom, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import RemoveIcon from '@mui/icons-material/Remove';

import DescTooltip from '../../DescTooltip';
import { toPercent } from './factorScale';
import { getFactorRangePalette, rem } from './factorRangePalette';
import { FACTOR_RANGE_GRID, TRACK } from './factorRangeLayout';
import type { FactorRangeDatum, ScaleDomain } from './factorRange.types';

interface FactorRangeRowProps {
  readonly factor: FactorRangeDatum;
  readonly domain: ScaleDomain;
}

/**
 * One factor plotted against the panel's shared axis: its industry range as a
 * band, the industry average as a tick, and the company score as a dot that
 * turns red when it falls short of that average.
 */
export function FactorRangeRow({
  factor,
  domain,
}: Readonly<FactorRangeRowProps>) {
  const { palette: themePalette } = useTheme();
  const palette = getFactorRangePalette(themePalette.mode);
  const behind = factor.company < factor.industry;

  const minLeft = toPercent(factor.min, domain);
  const maxLeft = toPercent(factor.max, domain);
  const industryLeft = toPercent(factor.industry, domain);
  const companyLeft = toPercent(factor.company, domain);

  const accent = behind ? palette.critical : palette.brand;

  return (
    <Box
      component="li"
      sx={{
        display: 'grid',
        alignItems: 'center',
        columnGap: rem(24),
        rowGap: rem(8),
        padding: `${rem(10)} 0`,
        gridTemplateColumns: { xs: '1fr', sm: FACTOR_RANGE_GRID },
        borderTop: `1px solid ${palette.line}`,
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Box minWidth={0}>
        <Box display="flex" alignItems="center" gap={rem(2)}>
          <Typography
            component="p"
            sx={{
              fontSize: rem(13),
              fontWeight: 600,
              lineHeight: 1.35,
              color: palette.ink,
            }}
          >
            {factor.label}
          </Typography>
          <DescTooltip tooltip={factor.tooltip} />
        </Box>
        {/* The description is clipped to keep rows scannable, so it carries its
            own tooltip -- truncated text the reader cannot recover is worse
            than no description at all. */}
        <Tooltip
          title={factor.subtext ?? ''}
          describeChild
          placement="top-start"
          arrow
          TransitionComponent={Zoom}
        >
          <Typography
            noWrap
            tabIndex={0}
            sx={{
              display: 'block',
              marginTop: rem(2),
              fontSize: rem(12),
              lineHeight: 1.4,
              color: palette.inkFaint,
              cursor: 'default',
            }}
          >
            {factor.subtext}
          </Typography>
        </Tooltip>
      </Box>

      <Box position="relative" height={rem(TRACK.height)}>
        {domain.ticks.map((tick) => (
          <Box
            key={tick}
            aria-hidden="true"
            position="absolute"
            top={0}
            bottom={rem(12)}
            width={rem(1)}
            bgcolor={palette.line}
            left={`${toPercent(tick, domain)}%`}
            sx={{ transform: 'translateX(-50%)' }}
          />
        ))}

        <Box
          aria-hidden="true"
          position="absolute"
          top={rem(TRACK.barTop)}
          height={rem(TRACK.barHeight)}
          borderRadius={rem(TRACK.barHeight)}
          bgcolor={palette.band}
          left={`${minLeft}%`}
          width={`${maxLeft - minLeft}%`}
        />

        {[minLeft, maxLeft].map((edge, index) => (
          <Box
            key={`cap-${index}`}
            aria-hidden="true"
            position="absolute"
            top={rem(TRACK.barTop - 4)}
            height={rem(TRACK.barHeight + 8)}
            width={rem(2)}
            borderRadius={rem(1)}
            bgcolor={palette.bandCap}
            left={`${edge}%`}
            sx={{ transform: 'translateX(-50%)' }}
          />
        ))}

        <RangeEdgeLabel value={factor.min} left={minLeft} />
        <RangeEdgeLabel value={factor.max} left={maxLeft} />

        <Box
          aria-hidden="true"
          position="absolute"
          top={rem(TRACK.barTop - 6)}
          height={rem(TRACK.barHeight + 12)}
          width={rem(2)}
          borderRadius={rem(1)}
          bgcolor={palette.reference}
          left={`${industryLeft}%`}
          sx={{ transform: 'translateX(-50%)' }}
        />

        <Box
          aria-hidden="true"
          data-qa="FACTOR_RANGE_DOT"
          position="absolute"
          top={rem(TRACK.barTop - (TRACK.dotSize - TRACK.barHeight) / 2)}
          height={rem(TRACK.dotSize)}
          width={rem(TRACK.dotSize)}
          borderRadius="50%"
          bgcolor={accent}
          left={`${companyLeft}%`}
          sx={{
            transform: 'translateX(-50%)',
            boxShadow: `0 0 0 ${rem(3)} ${palette.surface}`,
          }}
        />

        <Box component="span" sx={visuallyHidden}>
          {`${factor.label}: your score ${factor.company}, industry average ${factor.industry}, industry range ${factor.min} to ${factor.max}.`}
        </Box>
      </Box>

      <Box
        sx={{
          textAlign: { xs: 'left', sm: 'right' },
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: rem(17),
            // A step lighter than the company aggregate above, which is also
            // 600. Size alone did not separate them: eight rows of 600 read as
            // eight headline figures competing with the one number the panel is
            // actually led by.
            fontWeight: 500,
            color: behind ? palette.critical : palette.ink,
          }}
        >
          {factor.company}
        </Typography>
        <Typography
          component="span"
          sx={{
            marginLeft: rem(3),
            fontSize: rem(12),
            color: palette.inkFaint,
          }}
        >
          vs {factor.industry}
        </Typography>
        <FactorTrend trend={factor.trend} />
      </Box>
    </Box>
  );
}

function RangeEdgeLabel({
  value,
  left,
}: Readonly<{ value: number; left: number }>) {
  const { palette: themePalette } = useTheme();
  const palette = getFactorRangePalette(themePalette.mode);

  return (
    <Typography
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: rem(TRACK.barTop + 11),
        left: `${left}%`,
        transform: 'translateX(-50%)',
        fontSize: rem(11),
        fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
        color: palette.inkFaint,
      }}
    >
      {value}
    </Typography>
  );
}

const TREND_ICONS = {
  up: NorthEastIcon,
  down: SouthEastIcon,
  flat: RemoveIcon,
};

const getTrendDirection = (trend: number) => {
  if (trend > 0) {
    return 'up' as const;
  }
  if (trend < 0) {
    return 'down' as const;
  }
  return 'flat' as const;
};

/** The trend column. Renders nothing when the factor has no history. */
function FactorTrend({ trend }: Readonly<{ trend?: number }>) {
  const { palette: themePalette } = useTheme();
  const palette = getFactorRangePalette(themePalette.mode);

  if (typeof trend !== 'number') {
    return null;
  }

  const direction = getTrendDirection(trend);
  const Icon = TREND_ICONS[direction];
  const colors = {
    up: palette.positive,
    down: palette.critical,
    flat: palette.inkFaint,
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={rem(2)}
      sx={{
        marginTop: rem(2),
        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
      }}
    >
      <Icon sx={{ fontSize: rem(12), color: colors[direction] }} />
      <Typography
        component="span"
        sx={{
          fontSize: rem(11),
          fontWeight: 500,
          color: colors[direction],
        }}
      >
        {direction === 'flat' ? 'Flat' : Math.abs(trend)}
      </Typography>
      <Box component="span" sx={visuallyHidden}>
        {direction === 'flat'
          ? 'no change over the trend window'
          : `points ${direction} over the trend window`}
      </Box>
    </Box>
  );
}

export default FactorRangeRow;
